'use client'

import { NEXT_PUBLIC_GA4_MEASUREMENT_ID } from '@/lib/env'
import { readStoredPreferences } from '@/lib/analytics/consent'

/**
 * First-touch marketing attribution, captured in the browser and stored on the
 * early-access lead row so a conversion can be joined to the campaign that
 * produced it.
 *
 * "First-touch" = the FIRST landing wins. Once a record is stored, later
 * navigations never overwrite it, so a lead is attributed to the campaign that
 * first brought them — not the internal page they happened to submit from.
 *
 * Consent-independent by design: this is first-party attribution of an inbound
 * request under legitimate interest. It stores no third-party cookies and no ad
 * identifiers — only the visitor's own UTM tags, referrer, and landing path.
 */

export interface FirstTouchAttribution {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  referrer?: string
  landing_page?: string
}

/** localStorage key holding the first-touch record (JSON {@link FirstTouchAttribution}). */
export const FIRST_TOUCH_ATTRIBUTION_KEY = 'aient_first_touch_attribution'

// Per-field caps MUST stay <= the API zod bounds in app/api/early-access/route.ts,
// or a long first-touch value would store fine here yet 400 on every submit.
const UTM_MAX_LENGTH = 255
const URL_MAX_LENGTH = 1024

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

/** The exact keys we ever store/send — used to sanitize untrusted localStorage. */
const ATTRIBUTION_KEYS = [...UTM_KEYS, 'referrer', 'landing_page'] as const

function clamp(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

/**
 * Pure: derive attribution from raw location parts. Blank values are dropped;
 * `landing_page` is always present (the pathname), the rest are conditional.
 */
export function parseAttribution(args: {
  search: string
  referrer: string
  pathname: string
}): FirstTouchAttribution {
  const out: FirstTouchAttribution = {}
  const params = new URLSearchParams(args.search)
  for (const key of UTM_KEYS) {
    const value = params.get(key)?.trim()
    if (value) out[key] = clamp(value, UTM_MAX_LENGTH)
  }
  const referrer = args.referrer?.trim()
  if (referrer) out.referrer = clamp(referrer, URL_MAX_LENGTH)
  const pathname = args.pathname?.trim()
  if (pathname) out.landing_page = clamp(pathname, URL_MAX_LENGTH)
  return out
}

/**
 * Keep only known string attribution keys, clamped to the API bounds. This makes
 * the stored value untrusted-safe: a stale/tampered localStorage record can never
 * smuggle an unknown key (which the strict API schema would 400 on) or a key like
 * `email`/`source` (which could override the user's own submitted fields).
 */
function sanitize(raw: Record<string, unknown>): FirstTouchAttribution {
  const out: FirstTouchAttribution = {}
  for (const key of ATTRIBUTION_KEYS) {
    const value = raw[key]
    if (typeof value === 'string' && value) {
      const max = key === 'referrer' || key === 'landing_page' ? URL_MAX_LENGTH : UTM_MAX_LENGTH
      out[key] = clamp(value, max)
    }
  }
  return out
}

function readRaw(): FirstTouchAttribution | null {
  try {
    const raw = window.localStorage.getItem(FIRST_TOUCH_ATTRIBUTION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' ? sanitize(parsed as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Read the stored first-touch record, or `{}` when absent / SSR / corrupt. */
export function readStoredAttribution(): FirstTouchAttribution {
  if (typeof window === 'undefined') return {}
  return readRaw() ?? {}
}

/**
 * Capture first-touch attribution on landing. If a record already exists it is
 * returned unchanged (first-touch wins); otherwise the current location is parsed
 * and persisted. Safe to call on every page load — it's idempotent after the
 * first store. Best-effort: a private-mode / quota localStorage error is swallowed.
 */
export function captureFirstTouchAttribution(): FirstTouchAttribution {
  if (typeof window === 'undefined') return {}
  const existing = readRaw()
  if (existing) return existing

  const current = parseAttribution({
    search: window.location.search,
    referrer: document.referrer,
    pathname: window.location.pathname,
  })
  try {
    window.localStorage.setItem(FIRST_TOUCH_ATTRIBUTION_KEY, JSON.stringify(current))
  } catch {
    // localStorage unavailable — attribution is best-effort, never throw on a page.
  }
  return current
}

/**
 * Resolve consent plus the browser GA4 client/session identifiers.
 *
 * Consent is authoritative and is returned even when gtag is blocked or not yet
 * loaded, so the server can distinguish an explicit opt-out from a permitted hit
 * whose browser identifier is unavailable. Identifiers are accepted only in the
 * documented numeric GA4 shapes.
 */
export interface Ga4SessionAttribution {
  analyticsConsent: boolean
  clientId?: string
  sessionId?: string
}

function getGa4Field(field: 'client_id' | 'session_id', timeoutMs: number): Promise<string | null> {
  const gtag = window.gtag
  const measurementId = NEXT_PUBLIC_GA4_MEASUREMENT_ID
  if (typeof gtag !== 'function' || !measurementId) return Promise.resolve(null)

  return new Promise((resolve) => {
    let settled = false
    const finish = (value: string | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    const timer = window.setTimeout(() => finish(null), timeoutMs)
    try {
      gtag('get', measurementId, field, (id: unknown) => {
        window.clearTimeout(timer)
        finish(typeof id === 'string' && id ? id : null)
      })
    } catch {
      window.clearTimeout(timer)
      finish(null)
    }
  })
}

export async function getGa4SessionAttribution(
  timeoutMs = 1000,
): Promise<Ga4SessionAttribution> {
  if (typeof window === 'undefined') return { analyticsConsent: false }

  const analyticsConsent = readStoredPreferences().analytics
  if (!analyticsConsent) return { analyticsConsent: false }

  const [rawClientId, rawSessionId] = await Promise.all([
    getGa4Field('client_id', timeoutMs),
    getGa4Field('session_id', timeoutMs),
  ])
  const clientId = rawClientId && /^\d+\.\d+$/.test(rawClientId) ? rawClientId : undefined
  const sessionId = rawSessionId && /^\d+$/.test(rawSessionId) ? rawSessionId : undefined

  return {
    analyticsConsent: true,
    ...(clientId ? { clientId } : {}),
    ...(sessionId ? { sessionId } : {}),
  }
}
